// React Dependencies
import React, { useMemo, useRef, useState } from 'react';

// External Depedencies
import MaterialTable from 'material-table';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { IconButton, MenuItem, Select, Tooltip } from '@material-ui/core';
import {
  Edit as EditIcon, ToggleOn as ToggleOnIcon, ToggleOff as ToggleOffIcon, Settings as SettingsIcon, Lock as LockIcon,
} from '@material-ui/icons';

// Internal Dependencies
import MText from '../../../components/Common/MText';
import MButton from '../../../components/MButton';
import Colours from '../../../styles/colours';
import ApiService from '../../../services/apiService';
import GroundStaffFormModal from './GroundStaffFormModal';
import ConfirmDeactivateModal from './ConfirmDeactivateModal';
import OutletManagementModal from './OutletManagementModal';
import { GroundStaffType, Status } from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import { containerHeaderTextStyle } from '../../../styles/common';

const GroundStaffs = (props) => {
  const { pageId } = props.match.params;
  const tableRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formContext, setFormContext] = useState('Create');
  const [selectedGroundStaff, setSelectedGroundStaff] = useState(null);
  const [isManagingOutlets, setIsManagingOutlets] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

  // eslint-disable-next-line no-async-promise-executor
  const fetchTableData = (query) => new Promise(async (resolve) => {
    setIsLoading(true);
    try {
      query.pageNo = query.page;
      const { page } = query;

      const queryFilters = Object.fromEntries(query.filters.map((filters) => [filters.column.field, filters.value]));

      switch (queryFilters.status) {
        case 'All':
          delete queryFilters.status;
          break;
        case Status.Deactivated:
          break;
        case Status.Activated:
        default:
          queryFilters.status = Status.Activated;
          break;
      }

      const result = await ApiService.getGroundStaffs(pageId, { ...query, ...queryFilters });
      resolve({ data: result.rows.map((row) => ({ ...row, groundStaffId: row.id })), page, totalCount: result.totalCount });
    } catch (err) {
      toast.error(err.message);
      resolve({ data: [], page: 0, totalCount: 0 });
    } finally {
      setIsLoading(false);
    }
  });

  const openFormModal = (context, groundStaff) => {
    setFormContext(context);
    setSelectedGroundStaff(!IsEmpty(groundStaff) ? groundStaff : {});
    setIsFormModalOpen(true);
  };

  const handleReload = () => {
    if (!IsEmpty(tableRef) && !IsEmpty(tableRef.current)) {
      tableRef.current.onQueryChange();
    }
  };

  const columns = useMemo(() => [
    { title: 'ID', field: 'id', filtering: true, editable: 'never', cellStyle: { width: '15%' } },
    { title: 'Name', field: 'name', filtering: true, editable: 'never', cellStyle: { width: '20%' } },
    {
      title:           'Type',
      field:           'type',
      filtering:       true,
      cellStyle:       { width: '15%' },
      // eslint-disable-next-line no-shadow
      filterComponent: (props) => (
        <Select
          // eslint-disable-next-line react/prop-types
          onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
          className="w-100"
        >
          <MenuItem value="">-</MenuItem>
          {Object.values(GroundStaffType).map((value) => <MenuItem value={value}>{value}</MenuItem>)}
        </Select>
      ),
    },
    {
      title:           'Status',
      field:           'status',
      filtering:       true,
      cellStyle:       { width: '15%' },
      // eslint-disable-next-line no-shadow
      filterComponent: (props) => (
        <Select
          // eslint-disable-next-line react/prop-types
          onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
          className="w-100"
          defaultValue={Status.Activated}
        >
          <MenuItem value="All">-</MenuItem>
          {[Status.Activated, Status.Deactivated].map((value) => <MenuItem value={value}>{value}</MenuItem>)}
        </Select>
      ),
    },
    {
      title:     'Actions',
      editable:  'never',
      cellStyle: { width: '35%' },
      render:    (row) => {
        const isDeactivated = row.status === Status.Deactivated;
        return (
          <div className="d-flex justify-content-end align-items-center" style={{ gap: 10 }}>
            <MButton
              colour={isDeactivated ? Colours.Gray4 : Colours.Primary}
              textColour={Colours.White}
              className="d-flex justify-content-center align-items-center p-2"
              icon={() => <SettingsIcon htmlColor={Colours.White} className="mr-1 pr-1" />}
              style={{ width: '10rem' }}
              text="Manage Outlets"
              disabled={isDeactivated}
              onClick={() => {
                setSelectedGroundStaff(row);
                setIsManagingOutlets(true);
              }}
            />
            <MButton
              colour={isDeactivated ? Colours.Gray4 : Colours.Primary}
              textColour={Colours.White}
              onClick={() => openFormModal('UpdatePassword', row)}
              className="d-flex justify-content-center align-items-center p-2"
              icon={() => <LockIcon htmlColor={Colours.White} className="mr-1 pr-1" />}
              style={{ width: '10rem' }}
              disabled={isDeactivated}
              text="Update Password"
            />
            <Tooltip title="Edit" placement="top" arrow>
              <IconButton size="medium" disabled={isDeactivated} onClick={() => openFormModal('Update', row)}>
                <EditIcon htmlColor={isDeactivated ? Colours.Gray4 : Colours.Black} />
              </IconButton>
            </Tooltip>
            <Tooltip title={isDeactivated ? 'Activate' : 'Deactivate'} placement="top" arrow>
              <IconButton
                size="medium"
                onClick={() => {
                  setSelectedGroundStaff(row);
                  setIsDeactivating(true);
                }}
              >
                {
                  isDeactivated
                    ? <ToggleOffIcon htmlColor={Colours.Black} />
                    : <ToggleOnIcon htmlColor={Colours.Primary} />
                }
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ], []);

  return (
    <div className="container-fluid">
      <div className="mt-4 ml-2 mb-2 d-flex flex-row justify-content-start align-items-center">
        <MText size={containerHeaderTextStyle.fontSize} bold text="Ground Staffs" />
        <MButton
          className="ml-4 px-3"
          colour={Colours.Primary}
          textColour={Colours.White}
          onClick={() => openFormModal('Create')}
          text="+ Add"
          textSize={containerHeaderTextStyle.actionBtn.textSize}
        />
      </div>

      <div className="p-2 w-100">
        <MaterialTable
          tableRef={tableRef}
          isLoading={isLoading}
          title=""
          columns={columns}
          components={{ Toolbar: () => null }}
          options={{ filtering: true, search: false, debounceInterval: 500, emptyRowsWhenPaging: false }}
          data={fetchTableData}
        />
      </div>

      {
        isFormModalOpen && (
          <GroundStaffFormModal
            open={isFormModalOpen}
            onClose={() => setIsFormModalOpen(false)}
            groundStaff={selectedGroundStaff}
            context={formContext}
            pageId={Number(pageId)}
            handleLimitExceeded={() => setIsPlanModalOpen(true)}
            handleReload={handleReload}
          />
        )
      }

      {
        isDeactivating && (
          <ConfirmDeactivateModal
            open={isDeactivating}
            pageId={Number(pageId)}
            groundStaff={selectedGroundStaff}
            onClose={() => setIsDeactivating(false)}
            handleLimitExceeded={() => setIsPlanModalOpen(true)}
            handleReload={handleReload}
          />
        )
      }

      {
        isManagingOutlets && (
          <OutletManagementModal
            open={isManagingOutlets}
            onClose={() => setIsManagingOutlets(false)}
            groundStaffId={Number(selectedGroundStaff.id)}
            pageId={Number(pageId)}
          />
        )
      }

      <SubscriptionLimitModal
        open={isPlanModalOpen}
        close={() => setIsPlanModalOpen(false)}
        accountType={props.page.plan}
        page={props.page}
        type="groundStaff"
      />
    </div>
  );
};

GroundStaffs.propTypes = {
  match: PropTypes.shape(Object).isRequired,
  page:  PropTypes.shape(Object).isRequired,
};

const mapStateToProps = (state) => ({
  page: state.page.selected,
});

export default connect(mapStateToProps)(GroundStaffs);
